import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import { useAIAuthHeaders } from '@/features/ai-agents/shared/use-ai-auth-headers';

export function usePinsApi(isLocalHost = false) {
  const { headers } = useAIAuthHeaders(isLocalHost);

  const { get, post, patch, del, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      headers,
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: false,
    },
  }));

  const createPin = async (
    agentId: string,
    sessionId: string,
    messageId: string,
  ): Promise<PinnedReport> =>
    post(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiCreatePin(agentId, sessionId))
        : routes.aiCreatePin(agentId, sessionId),
      { message_id: messageId },
    );

  const listPins = async (
    limit = 20,
    offset = 0,
  ): Promise<PinnedReportListResponse> =>
    get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiListPins(limit, offset))
        : routes.aiListPins(limit, offset),
    );

  const getPin = async (pinId: string): Promise<PinnedReport> =>
    get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiGetPin(pinId))
        : routes.aiGetPin(pinId),
    );

  const runPin = async (
    pinId: string,
    startDate: string,
    endDate: string,
  ): Promise<RunPinResponse> =>
    post(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiRunPin(pinId))
        : routes.aiRunPin(pinId),
      { start_date: startDate, end_date: endDate },
    );

  const updatePin = async (
    pinId: string,
    body: UpdatePinRequest,
  ): Promise<PinnedReport> =>
    patch(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiUpdatePin(pinId))
        : routes.aiUpdatePin(pinId),
      body,
    );

  const deletePin = async (pinId: string): Promise<void> =>
    del(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiDeletePin(pinId))
        : routes.aiDeletePin(pinId),
    );

  return {
    createPin,
    listPins,
    getPin,
    runPin,
    updatePin,
    deletePin,
    loading,
  };
}

export interface PinnedReportSource {
  agent_id: string;
  session_id: string;
  run_id: string | null;
  message_id: string;
  store_alias: string;
  created_by: string;
}

export interface PinnedReportSection {
  name: string;
  description: string;
  query: string;
  query_keys: string[];
  data_table: string;
  default_view: Record<string, any>;
}

export interface PinnedReport {
  pin_id: string;
  name: string;
  description: string;
  sections: PinnedReportSection[];
  source: PinnedReportSource;
  created_at: string;
  updated_at: string;
}

export interface PinnedReportListItem {
  pin_id: string;
  name: string;
  description: string;
  section_count: number;
  created_at: string;
  source: PinnedReportSource;
}

export interface PinnedReportListResponse {
  items: PinnedReportListItem[];
  total: number;
  limit: number;
  offset: number;
}

export type RunPinSectionStatus = `success` | `error`;

export interface RunPinSection {
  name: string;
  status: RunPinSectionStatus;
  columns: string[];
  rows: Record<string, any>[];
  row_count: number;
  message: string | null;
  default_view: Record<string, any>;
}

export interface UpdatePinSectionRequest {
  name?: string;
  description?: string;
  default_view?: Record<string, any>;
}

export interface UpdatePinRequest {
  name?: string;
  description?: string;
  sections?: (UpdatePinSectionRequest | null)[];
}

export interface RunPinResponse {
  pin_id: string;
  name: string;
  sections: RunPinSection[];
}
